import { AbstractControl, UntypedFormGroup, Validators, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastService } from 'src/app/shared/services/toast.service';
import { ActivatedRoute } from '@angular/router';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { IPartner } from 'src/app/pages/partners/models/partner.interface';
import { ICompany } from 'src/app/pages/companies/models/company.interface';
import { PartnerService } from 'src/app/pages/partners/services/partner.service';
import { SettingsDashboardService } from '../../../services/settingsDashboard.service';
import { ModalConfirmacaoService } from 'src/app/shared/services/modal-confirmacao.service';
import { ITheme, PoweredByLogo, ThemeAssetType, ThemeModel } from '../../models/theme.interface';
import { finalize } from 'rxjs/operators';
import { EMessageType, IResponseMessage } from 'src/app/shared/data/response-default.interface';

/** Mesmo teto do `SkincareThemeModelValidator` do company-api. */
export const SKINCARE_TEXT_MAX_LENGTH = 120;

/** Mesmos limites do `HomeThemeModelValidator` do company-api. */
const HOME_TEXT_MAX_LENGTH = 120;
const HOME_IMAGE_URL_MAX_LENGTH = 500;
const HTTPS_URL_PATTERN = /^https:\/\/\S+$/;

/** Mesmo teto do upload no company-api. */
const MAX_ANIMATION_SIZE_MB = 1;

const ANIMATION_PATH_CONTROLS: Record<ThemeAssetType, string> = {
  intro: 'skincareIntroAnimationPath',
  standby: 'standbyAnimationPath',
  home: 'homeAnimationPath'
};

@Component({
  selector: 'app-edit-partner-settings',
  templateUrl: './edit-partner-settings.component.html',
  styleUrls: ['./edit-partner-settings.component.scss']
})
export class EditPartnerSettingsComponent implements OnInit, OnDestroy {
  private importCloseTimer?: ReturnType<typeof setTimeout>;
  ERoleAdminString = ERoleAdminString;

  themesFormArray = new UntypedFormArray([]);

  partnerId?: string;
  partner?: IPartner;
  companies: ICompany[] = [];
  selectedThemeIndex = 0;
  isSaving = false;

  importPreview: { payload: ITheme } | null = null;

  uploadingAsset: ThemeAssetType | null = null;
  /**
   * Lottie do arquivo escolhido agora, por `{themeId}:{tipo}`, para o preview. A animação já salva
   * não tem preview: o bucket de prd é privado e o admin não consegue baixar.
   */
  animationPreviews: Record<string, object> = {};
  importResult: { success: boolean; message: string } | null = null;
  importNameConflict: { original: string; suggested: string; existingId: string } | null = null;

  readonly colorFields = [
    { key: 'primaryColor', label: 'Primária', required: false },
    { key: 'secondaryColor', label: 'Secundária', required: false },
    { key: 'accentColor', label: 'Destaque', required: false }
  ];

  readonly totemColorFields = [
    { key: 'totemPrimaryColor', label: 'Primária', tooltip: '', required: true },
    { key: 'totemSecondaryColor', label: 'Secundária', tooltip: '', required: false },
    { key: 'totemAccentColor', label: 'Terciária', tooltip: '', required: false },
    {
      key: 'totemHomeBackground',
      label: 'Fundo da Home',
      tooltip:
        'Define a cor do degradê exibido na tela inicial, abaixo dos textos "Saúde, Bem-estar, Tecnologia e Acessível".',
      required: true
    },
    {
      key: 'totemEmailColorPrimary',
      label: 'E-mail Primária',
      tooltip: 'Cor de destaque do cabeçalho do e-mail, onde o logo do parceiro é exibido nos envios pelo Skin-care.',
      required: false
    },
    {
      key: 'totemEmailColorSecondary',
      label: 'E-mail Secundária',
      tooltip:
        'Cor de fundo do corpo do e-mail. Recomendado usar um tom mais claro que a cor primária para garantir melhor legibilidade.',
      required: false
    }
  ];

  activeTab: 'mobile' | 'desktop' | 'skincare' | 'standby' | 'home' = 'desktop';

  readonly homeTextMaxLength = HOME_TEXT_MAX_LENGTH;

  /** `emptyHint` diz o que o totem faz quando o campo fica em branco. */
  readonly homeTextFields = [
    {
      key: 'homeTitle',
      label: 'Título',
      placeholder: 'Saúde. Bem-estar. Tecnologia. Acessível.',
      emptyHint: 'Em branco, o totem usa "Saúde. Bem-estar. Tecnologia. Acessível."'
    },
    {
      key: 'homeSubtitle',
      label: 'Subtítulo',
      placeholder: 'Ex.: Faça sua triagem em poucos minutos',
      emptyHint: 'Em branco, o totem não mostra subtítulo.'
    },
    {
      key: 'homeCtaText',
      label: 'Botão',
      placeholder: 'Iniciar atendimento',
      emptyHint: 'Em branco, o totem usa "Iniciar atendimento".'
    }
  ];

  readonly skincareTextMaxLength = SKINCARE_TEXT_MAX_LENGTH;

  /** `fallback` é o texto fixo que o app usa quando o campo fica em branco. */
  readonly skincareTextFields = [
    { key: 'skincareIntroTitle', label: 'Título', fallback: 'Descubra sua pele' },
    { key: 'skincareIntroSubtitle', label: 'Subtítulo', fallback: 'Análise facial em segundos + rotina personalizada' },
    { key: 'skincareCtaTitle', label: 'Botão', fallback: 'Fazer o teste' },
    { key: 'skincareCaption', label: 'Legenda', fallback: 'Leva menos de 2 minutos · Gratuito' }
  ];

  /** Cores da tela inicial do skincare (OE-1825). Em branco, o app usa o visual padrão. */
  readonly skincareColorFields = [
    { key: 'skincareBackgroundColor', label: 'Fundo da tela', placeholder: '#000000' },
    { key: 'skincareTextColor', label: 'Textos e logo Oliv-e', placeholder: '#FFFFFF' },
    { key: 'skincareButtonBorderColor', label: 'Borda do botão', placeholder: '#FFFFFF' }
  ];

  /** Versões oficiais da logo Oliv-e no "Powered by", uma por tipo de fundo (OE-1825). */
  readonly poweredByLogoOptions: { value: PoweredByLogo; label: string; src: string; preview: string }[] = [
    { value: 'white', label: 'Fundo branco', src: 'assets/images/powered-by/olive-logo-white.svg', preview: '#FFFFFF' },
    {
      value: 'light',
      label: 'Fundo verde claro',
      src: 'assets/images/powered-by/olive-logo-light.svg',
      preview: '#6FC8C2'
    },
    { value: 'dark', label: 'Fundo escuro', src: 'assets/images/powered-by/olive-logo-dark.svg', preview: '#236470' }
  ];
  activeColorKey: string | null = null;

  readonly desktopLogoSlots = [
    { field: 'totemLogoUrl', badge: 'SVG', title: 'Home', required: true },
    { field: 'totemLogoHeaderUrl', badge: 'SVG', title: 'Header', required: false },
    { field: 'totemLogoPrintUrl', badge: 'SVG', title: 'Impressão', required: false },
    { field: 'totemLogoEmailUrl', badge: 'PNG', title: 'E-mail', required: false }
  ];

  readonly mobileLogoSlots = [
    { field: 'logoUrl', badge: 'SVG', title: 'Home', required: false },
    { field: 'logoPrintUrl', badge: 'SVG', title: 'Impressão', required: false }
  ];

  constructor(
    private readonly toastService: ToastService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly partnerService: PartnerService,
    private readonly settingsDashboardService: SettingsDashboardService,
    private readonly modalConfirmacao: ModalConfirmacaoService
  ) {}

  ngOnInit(): void {
    this.partnerId = this.activatedRoute.snapshot.paramMap.get('id') ?? undefined;
    if (this.partnerId) {
      this.loadPartner(this.partnerId);
      this.loadPartnerTheme(this.partnerId);
      this.listCompanies(this.partnerId);
    }
  }

  get selectedThemeForm(): UntypedFormGroup {
    const idx = this.selectedThemeIndex < this.themesFormArray.length ? this.selectedThemeIndex : 0;
    return (this.themesFormArray.at(idx) as UntypedFormGroup) ?? new UntypedFormGroup({});
  }

  get effectivePrimaryColor(): string {
    return (
      this.selectedThemeForm?.get('totemPrimaryColor')?.value ||
      this.selectedThemeForm?.get('primaryColor')?.value ||
      '#374151'
    );
  }

  companiesCount = 0;

  private updateCompaniesCount(): void {
    const allIds = this.themesFormArray.controls.flatMap((c) => this.companyIdsOf(c));
    this.companiesCount = new Set(allIds).size;
  }

  /**
   * O form guarda `companyIds` como lista (vem assim da API e vai assim no
   * payload), mas o campo "Empresa" é um <select> simples: ao escolher, ele grava
   * um id só, em string. Toda leitura passa por aqui para aceitar os dois.
   */
  private companyIdsOf(control: AbstractControl | null | undefined): string[] {
    const value = control?.get('companyIds')?.value;
    const ids: unknown[] = Array.isArray(value) ? value : [value];
    return ids.filter((id): id is string => typeof id === 'string' && id !== '' && id !== 'null');
  }

  private companyNameMap = new Map<string, string>();

  private buildCompanyNameMap(): void {
    this.companyNameMap = new Map(this.companies.filter((c) => !!c.id).map((c) => [c.id!, c.name]));
  }

  get availableCompanies(): ICompany[] {
    const usedIds = new Set(
      this.themesFormArray.controls.filter((_, i) => i !== this.selectedThemeIndex).flatMap((c) => this.companyIdsOf(c))
    );
    return this.companies.filter((c) => !(c.id && usedIds.has(c.id)));
  }

  get selectedThemeCompanyNames(): string {
    const ids = this.companyIdsOf(this.selectedThemeForm);
    if (!ids.length) return 'Sem empresas vinculadas';
    return ids.map((id) => this.companyNameMap.get(id) ?? id).join(', ');
  }

  getThemeCompanyNames(index: number): string {
    const ids = this.companyIdsOf(this.themesFormArray.at(index));
    if (!ids.length) return 'Sem empresa';
    return ids.map((id) => this.companyNameMap.get(id) ?? id).join(', ');
  }

  loadPartner(partnerId: string): void {
    this.partnerService.getPartnerById(partnerId).subscribe({
      next: (resp) => {
        if (resp.isSucceed) this.partner = resp.data;
      },
      error: () => this.toastService.toast('Erro ao carregar dados do parceiro.', EMessageType.Error)
    });
  }

  listCompanies(partnerId: string): void {
    this.partnerService.listCompaniesByPartnerId(partnerId).subscribe({
      next: (resp) => {
        if (resp.isSucceed) {
          this.companies = resp.data ?? [];
          this.buildCompanyNameMap();
        }
      },
      error: () => this.toastService.toast('Erro ao carregar empresas.', EMessageType.Error)
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.importCloseTimer);
  }

  loadPartnerTheme(partnerId: string, selectThemeId?: string): void {
    const unsaved = this.themesFormArray.controls.filter((c) => !c.get('id')?.value).map((c) => c as UntypedFormGroup);

    this.settingsDashboardService.listThemes(partnerId).subscribe({
      next: (resp) => {
        if (resp.messages?.length) {
          resp.messages.forEach((msg: IResponseMessage): void => {
            this.toastService.toast(msg.description, resp.isSucceed ? EMessageType.Success : EMessageType.Error);
          });
        }
        if (resp.isSucceed) {
          this.themesFormArray.clear();
          resp.data.forEach((theme: any) => {
            this.themesFormArray.push(this.buildThemeFormGroup(theme));
          });
          unsaved.forEach((fg) => this.themesFormArray.push(fg));
          this.updateCompaniesCount();
          if (selectThemeId) {
            const idx = resp.data.findIndex((t: ITheme) => t.id === selectThemeId);
            this.selectedThemeIndex = idx >= 0 ? idx : 0;
          } else if (this.selectedThemeIndex >= this.themesFormArray.length) {
            this.selectedThemeIndex = 0;
          }
        }
      },
      error: (err) => {
        // 400/404: a API não tem tema para o parceiro ("Nenhum tema encontrado"); não é falha de carga
        if (err?.status === 400 || err?.status === 404) {
          this.themesFormArray.clear();
        } else {
          this.toastService.toast('Erro ao carregar temas.', EMessageType.Error);
        }
      }
    });
  }

  /** A API devolve o tema em PascalCase (`Skincare.Texts.IntroTitle`); converte todos os níveis. */
  private normalizeKeys(obj: Record<string, any>): Record<string, any> {
    if (!obj || typeof obj !== 'object') return {};
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k.charAt(0).toLowerCase() + k.slice(1),
        v && typeof v === 'object' && !Array.isArray(v) ? this.normalizeKeys(v) : v
      ])
    );
  }

  private buildThemeFormGroup(theme?: any): UntypedFormGroup {
    let parsed: Record<string, any> = {};
    try {
      parsed = typeof theme?.theme === 'string' ? JSON.parse(theme.theme) : (theme?.theme ?? {});
    } catch {
      this.toastService.toast('Erro ao processar dados do tema.', EMessageType.Error);
    }
    const raw = this.normalizeKeys(parsed);
    const rawDesktop = raw.desktop ?? {};
    const skincare = raw.skincare ?? {};
    const skincareTexts = skincare.texts ?? {};
    const standby = raw.standby ?? {};
    const home = raw.home ?? {};
    const desktop = {
      logoUrl: rawDesktop.logoUrl ?? raw.logoUrl,
      logoHeaderUrl: rawDesktop.logoHeaderUrl,
      logoEmailUrl: rawDesktop.logoEmailUrl,
      logoPrintUrl: rawDesktop.logoPrintUrl ?? raw.logoPrintUrl,
      primaryColor: rawDesktop.primaryColor ?? raw.primaryColor,
      secondaryColor: rawDesktop.secondaryColor ?? raw.secondaryColor,
      accentColor: rawDesktop.accentColor ?? raw.accentColor,
      homeBackground: rawDesktop.homeBackground,
      emailColorPrimary: rawDesktop.emailColorPrimary,
      emailColorSecondary: rawDesktop.emailColorSecondary
    };
    return new UntypedFormGroup({
      id: new UntypedFormControl(theme?.id || null),
      themeName: new UntypedFormControl(theme?.themeName || '', [Validators.required, Validators.maxLength(100)]),
      logoUrl: new UntypedFormControl(raw.logoUrl || ''),
      logoPrintUrl: new UntypedFormControl(raw.logoPrintUrl || ''),
      primaryColor: new UntypedFormControl(raw.primaryColor || ''),
      secondaryColor: new UntypedFormControl(raw.secondaryColor || ''),
      accentColor: new UntypedFormControl(raw.accentColor || ''),
      totemLogoUrl: new UntypedFormControl(desktop.logoUrl || '', [Validators.required]),
      totemLogoHeaderUrl: new UntypedFormControl(desktop.logoHeaderUrl || ''),
      totemLogoEmailUrl: new UntypedFormControl(desktop.logoEmailUrl || ''),
      totemLogoPrintUrl: new UntypedFormControl(desktop.logoPrintUrl || ''),
      totemPrimaryColor: new UntypedFormControl(desktop.primaryColor || '', [Validators.required]),
      totemSecondaryColor: new UntypedFormControl(desktop.secondaryColor || ''),
      totemAccentColor: new UntypedFormControl(desktop.accentColor || ''),
      totemHomeBackground: new UntypedFormControl(desktop.homeBackground || '', [Validators.required]),
      totemEmailColorPrimary: new UntypedFormControl(desktop.emailColorPrimary || ''),
      totemEmailColorSecondary: new UntypedFormControl(desktop.emailColorSecondary || ''),
      skincareIntroAnimationPath: new UntypedFormControl(skincare.introAnimationPath || ''),
      skincareIntroTitle: new UntypedFormControl(skincareTexts.introTitle || '', [
        Validators.maxLength(SKINCARE_TEXT_MAX_LENGTH)
      ]),
      skincareIntroSubtitle: new UntypedFormControl(skincareTexts.introSubtitle || '', [
        Validators.maxLength(SKINCARE_TEXT_MAX_LENGTH)
      ]),
      skincareCtaTitle: new UntypedFormControl(skincareTexts.ctaTitle || '', [
        Validators.maxLength(SKINCARE_TEXT_MAX_LENGTH)
      ]),
      skincareCaption: new UntypedFormControl(skincareTexts.caption || '', [
        Validators.maxLength(SKINCARE_TEXT_MAX_LENGTH)
      ]),
      skincareBackgroundColor: new UntypedFormControl(skincare.backgroundColor || ''),
      skincareTextColor: new UntypedFormControl(skincare.textColor || ''),
      skincareButtonBorderColor: new UntypedFormControl(skincare.buttonBorderColor || ''),
      skincarePoweredByLogo: new UntypedFormControl(skincare.poweredByLogo || 'white'),
      standbyEnabled: new UntypedFormControl(!!standby.enabled),
      standbyAnimationPath: new UntypedFormControl(standby.animationPath || ''),
      homeEnabled: new UntypedFormControl(!!home.enabled),
      homeTitle: new UntypedFormControl(home.title || '', [Validators.maxLength(HOME_TEXT_MAX_LENGTH)]),
      homeSubtitle: new UntypedFormControl(home.subtitle || '', [Validators.maxLength(HOME_TEXT_MAX_LENGTH)]),
      homeCtaText: new UntypedFormControl(home.ctaText || '', [Validators.maxLength(HOME_TEXT_MAX_LENGTH)]),
      homeImageUrl: new UntypedFormControl(home.imageUrl || '', [
        Validators.maxLength(HOME_IMAGE_URL_MAX_LENGTH),
        Validators.pattern(HTTPS_URL_PATTERN)
      ]),
      homeAnimationPath: new UntypedFormControl(home.animationPath || ''),
      companyIds: new UntypedFormControl(
        (theme?.companies ?? theme?.Companies ?? []).map((c: any) => this.normalizeKeys(c)['id']).filter(Boolean)
      )
    });
  }

  selectTheme(index: number): void {
    this.selectedThemeIndex = index;
  }

  addNewTheme(): void {
    this.themesFormArray.push(this.buildThemeFormGroup());
    this.selectedThemeIndex = this.themesFormArray.length - 1;
  }

  saveTheme(): void {
    if (this.isSaving || !this.selectedThemeForm || !this.partnerId) return;
    if (this.selectedThemeForm.invalid) {
      this.selectedThemeForm.markAllAsTouched();
      this.toastService.toast('Preencha todos os campos obrigatórios antes de salvar.');
      return;
    }

    const themeId: string | null = this.selectedThemeForm.get('id')?.value;
    const dados = this.buildThemePayload();
    this.isSaving = true;

    if (themeId) {
      this.settingsDashboardService.updateTheme(themeId, dados).subscribe({
        next: (resp) => {
          if (resp.isSucceed) {
            this.toastService.toast('Tema atualizado com sucesso');
            this.loadPartnerTheme(this.partnerId!);
          }
        },
        error: () => {
          this.toastService.toast('Erro ao atualizar tema.', EMessageType.Error);
          this.isSaving = false;
        },
        complete: () => {
          this.isSaving = false;
        }
      });
    } else {
      this.settingsDashboardService.createTheme(dados).subscribe({
        next: (resp) => {
          if (resp.isSucceed) {
            this.toastService.toast('Tema criado com sucesso!');
            this.themesFormArray.removeAt(this.selectedThemeIndex);
            this.loadPartnerTheme(this.partnerId!, resp.data?.id);
          }
        },
        error: () => {
          this.toastService.toast('Erro ao criar tema.', EMessageType.Error);
          this.isSaving = false;
        },
        complete: () => {
          this.isSaving = false;
        }
      });
    }
  }

  private buildThemePayload(): ITheme {
    const v = this.selectedThemeForm?.value ?? {};
    const opt = (val: string): string | undefined => val || undefined;
    return {
      partnerId: this.partnerId!,
      themeName: v.themeName,
      theme: {
        logoUrl: opt(v.logoUrl),
        logoPrintUrl: opt(v.logoPrintUrl),
        primaryColor: opt(v.primaryColor),
        secondaryColor: opt(v.secondaryColor),
        accentColor: opt(v.accentColor),
        desktop: {
          logoUrl: opt(v.totemLogoUrl),
          logoHeaderUrl: opt(v.totemLogoHeaderUrl),
          logoEmailUrl: opt(v.totemLogoEmailUrl),
          logoPrintUrl: opt(v.totemLogoPrintUrl),
          primaryColor: opt(v.totemPrimaryColor),
          secondaryColor: opt(v.totemSecondaryColor),
          accentColor: opt(v.totemAccentColor),
          homeBackground: opt(v.totemHomeBackground),
          emailColorPrimary: opt(v.totemEmailColorPrimary),
          emailColorSecondary: opt(v.totemEmailColorSecondary)
        },
        ...this.buildModuleBlocks(v, opt)
      },
      companyIds: this.companyIdsOf(this.selectedThemeForm)
    };
  }

  /** `skincare`, `standby` e `home` só vão no payload quando têm algo configurado; tema sem eles fica como antes. */
  private buildModuleBlocks(v: Record<string, any>, opt: (val: string) => string | undefined): Partial<ThemeModel> {
    const texts = {
      introTitle: opt(v.skincareIntroTitle),
      introSubtitle: opt(v.skincareIntroSubtitle),
      ctaTitle: opt(v.skincareCtaTitle),
      caption: opt(v.skincareCaption)
    };
    const hasTexts = Object.values(texts).some(Boolean);
    // Só as cores preenchidas entram no bloco; vazias ficam com o padrão do app.
    // A logo de fundo branco é o padrão do app, então também não precisa ir.
    const colors = Object.fromEntries(
      Object.entries({
        backgroundColor: v.skincareBackgroundColor,
        textColor: v.skincareTextColor,
        buttonBorderColor: v.skincareButtonBorderColor,
        poweredByLogo: v.skincarePoweredByLogo === 'white' ? '' : v.skincarePoweredByLogo
      }).filter(([, value]) => !!value)
    );
    const hasColors = Object.keys(colors).length > 0;
    const blocks: Partial<ThemeModel> = {};
    if (v.skincareIntroAnimationPath || hasTexts || hasColors) {
      blocks.skincare = {
        introAnimationPath: opt(v.skincareIntroAnimationPath),
        texts: hasTexts ? texts : undefined,
        ...colors
      };
    }
    if (v.standbyEnabled || v.standbyAnimationPath) {
      blocks.standby = { enabled: !!v.standbyEnabled, animationPath: opt(v.standbyAnimationPath) };
    }
    const home = {
      title: opt(v.homeTitle),
      subtitle: opt(v.homeSubtitle),
      ctaText: opt(v.homeCtaText),
      imageUrl: opt(v.homeImageUrl),
      animationPath: opt(v.homeAnimationPath)
    };
    if (v.homeEnabled || Object.values(home).some(Boolean)) {
      blocks.home = { enabled: !!v.homeEnabled, ...home };
    }
    return blocks;
  }

  /**
   * O caminho da animação é de um tema só (`themes/{id}/...`): a API recusa caminho de outro tema.
   * Import entra sem caminho; no "substituir", recebe os do tema que está sendo substituído.
   */
  private withAnimationPaths(
    theme: ThemeModel,
    introPath?: string,
    standbyPath?: string,
    homePath?: string
  ): ThemeModel {
    const { skincare, standby, home, ...rest } = theme;
    const result: ThemeModel = { ...rest };
    if (skincare || introPath) {
      result.skincare = { ...skincare, introAnimationPath: introPath || undefined };
    }
    if (standby || standbyPath) {
      result.standby = { enabled: !!standby?.enabled, animationPath: standbyPath || undefined };
    }
    if (home || homePath) {
      result.home = { ...home, enabled: !!home?.enabled, animationPath: homePath || undefined };
    }
    return result;
  }

  exportTheme(): void {
    if (!this.selectedThemeForm) return;
    const full = this.buildThemePayload();
    const { companyIds, ...exportable } = full;
    const blob = new Blob([JSON.stringify(exportable, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${full.themeName || 'tema'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer?.files?.[0];
    if (!file || !file.name.endsWith('.json')) {
      this.toastService.toast('Arraste um arquivo .json válido.');
      return;
    }
    this.readJsonFile(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onImportJson(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.readJsonFile(file);
    (event.target as HTMLInputElement).value = '';
  }

  onAnimationSelected(type: ThemeAssetType, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    const themeForm = this.selectedThemeForm;
    const themeId: string | null = themeForm.get('id')?.value ?? null;
    if (!themeId) {
      this.toastService.toast('Salve o tema antes de enviar a animação.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.json')) {
      this.toastService.toast('Envie o arquivo .json da animação Lottie.');
      return;
    }
    if (file.size > MAX_ANIMATION_SIZE_MB * 1024 * 1024) {
      this.toastService.toast(`Animação muito grande. Máximo: ${MAX_ANIMATION_SIZE_MB}MB.`);
      return;
    }

    this.uploadingAsset = type;
    this.settingsDashboardService
      .uploadThemeAsset(themeId, type, file)
      .pipe(finalize(() => (this.uploadingAsset = null)))
      .subscribe({
        next: (resp) => {
          if (!resp.isSucceed) {
            this.toastService.toast(
              resp.messages?.[0]?.description || 'Erro ao enviar a animação.',
              EMessageType.Error
            );
            return;
          }
          const control = themeForm.get(ANIMATION_PATH_CONTROLS[type]);
          control?.setValue(resp.data);
          control?.markAsDirty();
          this.keepAnimationPreview(themeId, type, file);
          this.toastService.toast('Animação enviada. Salve o tema para aplicar.', EMessageType.Success);
        },
        error: (err) =>
          this.toastService.toast(
            err?.error?.messages?.[0]?.description || 'Erro ao enviar a animação.',
            EMessageType.Error
          )
      });
  }

  /**
   * `saved`: o tema salvo já tem a animação. `pending`: enviada agora, mas o tema ainda não foi salvo
   * (o caminho só vale depois do Salvar). `none`: sem animação.
   */
  animationStatusOf(type: ThemeAssetType): 'none' | 'pending' | 'saved' {
    const control = this.selectedThemeForm.get(ANIMATION_PATH_CONTROLS[type]);
    if (!control?.value) return 'none';
    return control.dirty ? 'pending' : 'saved';
  }

  animationPreviewOf(type: ThemeAssetType): object | null {
    const themeId = this.selectedThemeForm.get('id')?.value;
    return (themeId && this.animationPreviews[`${themeId}:${type}`]) || null;
  }

  private keepAnimationPreview(themeId: string, type: ThemeAssetType, file: File): void {
    file
      .text()
      .then(
        (text) => (this.animationPreviews = { ...this.animationPreviews, [`${themeId}:${type}`]: JSON.parse(text) })
      )
      .catch(() => undefined); // a API já validou o JSON; sem preview não impede nada
  }

  private static readonly MAX_JSON_SIZE_MB = 0.1;

  private readJsonFile(file: File): void {
    if (file.size > EditPartnerSettingsComponent.MAX_JSON_SIZE_MB * 1024 * 1024) {
      this.toastService.toast(`Arquivo muito grande. Máximo: ${EditPartnerSettingsComponent.MAX_JSON_SIZE_MB}MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e): void => {
      try {
        const result = e.target?.result;
        if (typeof result !== 'string') {
          this.toastService.toast('Arquivo JSON inválido.');
          return;
        }
        const parsed = JSON.parse(result);
        const payload: ITheme = {
          partnerId: this.partnerId!,
          themeName: (parsed.themeName || 'Tema importado').trim().slice(0, 100),
          theme: this.withAnimationPaths(this.normalizeKeys(parsed.theme ?? parsed)),
          companyIds: parsed.companyIds ?? []
        };
        this.importPreview = { payload };
        this.importResult = null;
        this.importNameConflict = null;

        const duplicate = this.themesFormArray.controls.find(
          (c) => (c.get('themeName')?.value as string) === payload.themeName
        );
        if (duplicate) {
          this.importNameConflict = {
            original: payload.themeName,
            suggested: this.generateUniqueName(payload.themeName),
            existingId: duplicate.get('id')?.value
          };
        }
      } catch {
        this.toastService.toast('Arquivo JSON inválido.');
      }
    };
    reader.readAsText(file);
  }

  private generateUniqueName(baseName: string): string {
    const existing = new Set(this.themesFormArray.controls.map((c) => c.get('themeName')?.value as string));
    let n = 2;
    let candidate = `${baseName} ${n}`;
    while (existing.has(candidate)) {
      n++;
      candidate = `${baseName} ${n}`;
    }
    return candidate;
  }

  resolveConflict(action: 'replace' | 'rename'): void {
    if (!this.importPreview || !this.importNameConflict) return;
    if (action === 'rename') {
      const newName = this.importNameConflict.suggested;
      this.importPreview = {
        payload: { ...this.importPreview.payload, themeName: newName }
      };
      this.importNameConflict = null;
      this.confirmImport();
    } else {
      const existingId = this.importNameConflict.existingId;
      if (!existingId) {
        this.importResult = {
          success: false,
          message: 'O tema existente ainda não foi salvo. Salve-o antes de substituir.'
        };
        return;
      }
      const existingCtrl = this.themesFormArray.controls.find((c) => c.get('id')?.value === existingId);
      const safePayload: ITheme = {
        ...this.importPreview.payload,
        theme: this.withAnimationPaths(
          this.importPreview.payload.theme,
          existingCtrl?.get('skincareIntroAnimationPath')?.value,
          existingCtrl?.get('standbyAnimationPath')?.value,
          existingCtrl?.get('homeAnimationPath')?.value
        ),
        companyIds: existingCtrl ? this.companyIdsOf(existingCtrl) : this.importPreview.payload.companyIds
      };
      this.importNameConflict = null;
      this.settingsDashboardService.updateTheme(existingId, safePayload).subscribe({
        next: (resp) => {
          if (resp.isSucceed) {
            this.importResult = { success: true, message: 'Tema substituído com sucesso!' };
            this.importCloseTimer = setTimeout(() => {
              this.importPreview = null;
              this.importResult = null;
              this.loadPartnerTheme(this.partnerId!, existingId);
            }, 1500);
          } else {
            this.importResult = {
              success: false,
              message: resp.messages?.[0]?.description || 'Erro ao substituir tema.'
            };
          }
        },
        error: () => {
          this.importResult = { success: false, message: 'Erro ao conectar com a API.' };
        }
      });
    }
  }

  confirmImport(): void {
    if (!this.importPreview) return;
    this.settingsDashboardService.createTheme(this.importPreview.payload).subscribe({
      next: (resp) => {
        if (resp.isSucceed) {
          this.importResult = { success: true, message: 'Tema importado com sucesso!' };
          this.importCloseTimer = setTimeout(() => {
            this.importPreview = null;
            this.importResult = null;
            this.loadPartnerTheme(this.partnerId!, resp.data?.id);
          }, 1500);
        } else {
          this.importResult = { success: false, message: resp.messages?.[0]?.description || 'Erro ao importar tema.' };
        }
      },
      error: () => {
        this.importResult = { success: false, message: 'Erro ao conectar com a API.' };
      }
    });
  }

  get importSummary(): {
    themeName: string;
    hasDesktopLogo: boolean;
    hasMobileLogo: boolean;
    primaryColor: string | undefined;
  } | null {
    if (!this.importPreview) return null;
    const t = this.importPreview.payload.theme;
    return {
      themeName: this.importPreview.payload.themeName,
      hasDesktopLogo: !!t.desktop?.logoUrl,
      hasMobileLogo: !!t.logoUrl,
      primaryColor: t.desktop?.primaryColor ?? t.primaryColor
    };
  }

  getContrastColor(hex: string): string {
    if (!hex) return '#111827';
    const clean = hex.replace('#', '');
    if (clean.length !== 6) return '#111827';
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#111827' : '#ffffff';
  }

  trackByIndex = (index: number): number => index;

  cancelImport(): void {
    clearTimeout(this.importCloseTimer);
    this.importPreview = null;
    this.importResult = null;
    this.importNameConflict = null;
  }

  deleteTheme(): void {
    if (!this.partnerId) return;
    const themeId: string | null = this.selectedThemeForm?.get('id')?.value ?? null;
    const indexToRemove = this.selectedThemeIndex;

    this.modalConfirmacao
      .abrirModalConfirmacao({ mensagem: 'Tem certeza que deseja excluir este tema?' })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        if (!themeId) {
          this.themesFormArray.removeAt(indexToRemove);
          this.selectedThemeIndex = Math.max(0, indexToRemove - 1);
          return;
        }
        this.settingsDashboardService.deleteTheme(themeId).subscribe({
          next: (resp) => {
            if (resp.isSucceed) {
              this.selectedThemeIndex = 0;
              this.loadPartnerTheme(this.partnerId!);
            } else {
              this.toastService.toast(resp.messages?.length ? resp.messages : 'Erro ao excluir tema.');
            }
          },
          error: () => this.toastService.toast('Erro ao excluir tema.', EMessageType.Error)
        });
      });
  }
}
